/**
 * R128 / R232q5dt: Adapter — wraps vanilla-quests.json (1492 WotLK 3.3.5a quests across
 * ~50 zones) into the existing `Quest` shape used by NpcDialog/QuestLog.
 *
 * Each cmangos zone slug → primary giver NPC at a hand-mapped world
 * position (matched to SERVER_ZONES bounds where possible). Russian
 * names are auto-generated by transliterating the English title.
 */
import { vanillaData, type VQuest } from './vanilla.js';
import { QUESTS, type Quest } from './quests.js';

/** Zone slug → giver assignment. Position must match what the server
 *  spawns (giver-NPCs are placed at these coords by RealmRoom).
 *  Slugs match cmangos `zone` field; positions hand-tuned to AIWARI
 *  world layout (-3500..3500 X, -1750..1750 Z). */
const ZONE_GIVER: Record<string, { giverId: string; pos: { x: number; z: number } }> = {
  // ── STARTER ZONES (1-10) ──
  greenwald:       { giverId: 'vq-giver-greenwald',      pos: { x: -650,  z: 0    } },
  dunhammer:       { giverId: 'vq-giver-dunhammer',      pos: { x: -1100, z: -900 } },
  worldroot:       { giverId: 'vq-giver-worldroot',      pos: { x: 900,   z: 1300 } },
  redrock:         { giverId: 'vq-giver-redrock',        pos: { x: 650,   z: 0    } },
  mulhorn:         { giverId: 'vq-giver-mulhorn',        pos: { x: 400,   z: 900  } },
  hollow:          { giverId: 'vq-giver-hollow',         pos: { x: -1500, z: 1500 } },
  // ── 10-20 ──
  westmarch:       { giverId: 'vq-giver-westmarch',      pos: { x: -1950, z: -300 } },
  lochmod:         { giverId: 'vq-giver-lochmod',        pos: { x: -2450, z: -400 } },
  darkwald:        { giverId: 'vq-giver-darkwald',       pos: { x: -2150, z: -1100 } },
  silverleaf:      { giverId: 'vq-giver-silverleaf',     pos: { x: 1700,  z: 1300 } },
  barrens:         { giverId: 'vq-giver-barrens',        pos: { x: 1000,  z: 0    } },
  redhills:        { giverId: 'vq-giver-redhills',       pos: { x: -1300, z: -1250 } },
  // ── 18-30 ──
  twilightswamp:   { giverId: 'vq-giver-twilightswamp',  pos: { x: -1400, z: 900  } },
  gloomwood:       { giverId: 'vq-giver-gloomwood',      pos: { x: -2150, z: -1100 } },
  ashenvalley:     { giverId: 'vq-giver-ashenvalley',    pos: { x: 900,   z: 950  } },
  ashenvale:       { giverId: 'vq-giver-ashenvalley',    pos: { x: 900,   z: 950  } },
  // ── 25-35 ──
  thousandwells:   { giverId: 'vq-giver-thousandwells',  pos: { x: 1500,  z: 900  } },
  thousand:        { giverId: 'vq-giver-thousandwells',  pos: { x: 1500,  z: 900  } },
  thousand_needles:{ giverId: 'vq-giver-thousandwells',  pos: { x: 1500,  z: 900  } },
  stormveil:       { giverId: 'vq-giver-stormveil',      pos: { x: 0,     z: 0    } },
  // ── 30-45 ──
  arathih:         { giverId: 'vq-giver-arathih',        pos: { x: -2450, z: 1400 } },
  alterac:         { giverId: 'vq-giver-alterac',        pos: { x: -2200, z: 600  } },
  hillsplain:      { giverId: 'vq-giver-hillsplain',     pos: { x: -1950, z: 1000 } },
  swampsorrow:     { giverId: 'vq-giver-swampsorrow',    pos: { x: -1400, z: 900  } },
  hinterhills:     { giverId: 'vq-giver-hinterhills',    pos: { x: -1300, z: 1500 } },
  stranglehollow:  { giverId: 'vq-giver-stranglehollow', pos: { x: -1700, z: 1700 } },
  feralpath:       { giverId: 'vq-giver-feralpath',      pos: { x: 1500,  z: 1400 } },
  feralas:         { giverId: 'vq-giver-feralpath',      pos: { x: 1500,  z: 1400 } },
  // ── 40-55 ──
  tanaris:         { giverId: 'vq-giver-tanaris',        pos: { x: 1500,  z: -1300 } },
  searinglands:    { giverId: 'vq-giver-searinglands',   pos: { x: -1400, z: 1400 } },
  azshira:         { giverId: 'vq-giver-azshira',        pos: { x: 1300,  z: 1400 } },
  ungoros:         { giverId: 'vq-giver-ungoros',        pos: { x: 200,   z: 1400 } },
  ungorocrater:    { giverId: 'vq-giver-ungoros',        pos: { x: 200,   z: 1400 } },
  felwoods:        { giverId: 'vq-giver-felwoods',       pos: { x: -800,  z: 1400 } },
  // ── 50-60 ──
  burningplain:    { giverId: 'vq-giver-burningplain',   pos: { x: -800,  z: 1400 } },
  plagueforest:    { giverId: 'vq-giver-plagueforest',   pos: { x: -2150, z: 1600 } },
  westerplague:    { giverId: 'vq-giver-westerplague',   pos: { x: -1700, z: 1600 } },
  winterhold:      { giverId: 'vq-giver-winterhold',     pos: { x: 700,   z: 1600 } },
  winterspring:    { giverId: 'vq-giver-winterhold',     pos: { x: 700,   z: 1600 } },
  silinthus:       { giverId: 'vq-giver-silinthus',      pos: { x: 800,   z: 1600 } },
};

/** Russian + English names per zone-giver. Used in NpcDialog header. */
const GIVER_NAMES_RU: Record<string, string> = {
  'vq-giver-greenwald':      'Командор Тирен Стальгорн',
  'vq-giver-dunhammer':      'Сенатор Барин Каменотёс',
  'vq-giver-worldroot':      'Нириэль Шёлковый Ветер',
  'vq-giver-redrock':        'Сардар Шарпкрест',
  'vq-giver-mulhorn':        'Громовая Грива',
  'vq-giver-hollow':         'Аптекарь Берринг',
  'vq-giver-westmarch':      'Магистр Уэстмарша',
  'vq-giver-lochmod':        'Тан Лохмода',
  'vq-giver-darkwald':       'Лесник Тёмного Леса',
  'vq-giver-silverleaf':     'Старейшина Серебристых Сосен',
  'vq-giver-barrens':        'Десятник Пустошей',
  'vq-giver-redhills':       'Командир Красных Холмов',
  'vq-giver-twilightswamp':  'Жрица Сумеречного Болота',
  'vq-giver-gloomwood':      'Лесник Сумрачной Чащи',
  'vq-giver-ashenvalley':    'Хранитель Пепельной Долины',
  'vq-giver-thousandwells':  'Шаман Тысячи Колодцев',
  'vq-giver-stormveil':      'Командор Грозового Перекрёстка',
  'vq-giver-arathih':        'Маршал Аратийских Высот',
  'vq-giver-alterac':        'Граф Альтерака',
  'vq-giver-hillsplain':     'Бургомистр Холмистой Равнины',
  'vq-giver-swampsorrow':    'Колдун Болота Скорби',
  'vq-giver-hinterhills':    'Старейшина Глубинных Холмов',
  'vq-giver-stranglehollow': 'Хозяин Удушливой Лощины',
  'vq-giver-feralpath':      'Друид Дикого Пути',
  'vq-giver-tanaris':        'Картограф Танариса',
  'vq-giver-searinglands':   'Огневой Лорд Палящих Земель',
  'vq-giver-azshira':        'Жрица Азширы',
  'vq-giver-ungoros':        'Эксцентричный Шарлатан',
  'vq-giver-felwoods':       'Эмиссар Изумрудного Сна',
  'vq-giver-burningplain':   'Лорд Чёрной Горы',
  'vq-giver-plagueforest':   'Чумной Изыскатель',
  'vq-giver-westerplague':   'Архидруид Западной Чумы',
  'vq-giver-winterhold':     'Хозяйка Снежной Цитадели',
  'vq-giver-silinthus':      'Сильваруш Сильнистор',
};

const GIVER_NAMES_EN: Record<string, string> = {
  'vq-giver-greenwald':      'Commander Tiren Steelhorn',
  'vq-giver-dunhammer':      'Senator Barin Stoneforge',
  'vq-giver-worldroot':      'Niriel Silkwind',
  'vq-giver-redrock':        'Sardar Sharpcrest',
  'vq-giver-mulhorn':        'Thunderhide Mane',
  'vq-giver-hollow':         'Apothecary Berring',
  'vq-giver-westmarch':      'Magister of Westmarch',
  'vq-giver-lochmod':        'Thane of Lochmod',
  'vq-giver-darkwald':       'Ranger of Darkwald',
  'vq-giver-silverleaf':     'Elder of Silverleaf Pines',
  'vq-giver-barrens':        'Foreman of the Wastelands',
  'vq-giver-redhills':       'Commander of Red Hills',
  'vq-giver-twilightswamp':  'Priestess of the Twilight Swamp',
  'vq-giver-gloomwood':      'Ranger of Gloomwood',
  'vq-giver-ashenvalley':    'Keeper of Ashen Valley',
  'vq-giver-thousandwells':  'Shaman of the Thousand Wells',
  'vq-giver-stormveil':      'Commander of Stormveil Crossroads',
  'vq-giver-arathih':        'Marshal of Arathi Highlands',
  'vq-giver-alterac':        'Earl of Alterac',
  'vq-giver-hillsplain':     'Burgomaster of Hillsplain',
  'vq-giver-swampsorrow':    'Witch of the Sorrow Marsh',
  'vq-giver-hinterhills':    'Elder of the Hinterhills',
  'vq-giver-stranglehollow': 'Master of Stranglehollow',
  'vq-giver-feralpath':      'Druid of the Feral Path',
  'vq-giver-tanaris':        'Cartographer of Tanaris',
  'vq-giver-searinglands':   'Firelord of Searing Lands',
  'vq-giver-azshira':        'Priestess of Azshira',
  'vq-giver-ungoros':        'Eccentric Charlatan',
  'vq-giver-felwoods':       'Emissary of the Emerald Dream',
  'vq-giver-burningplain':   'Lord of Black Mountain',
  'vq-giver-plagueforest':   'Plague Surveyor',
  'vq-giver-westerplague':   'Archdruid of the Western Plague',
  'vq-giver-winterhold':     'Mistress of the Winter Hold',
  'vq-giver-silinthus':      'Silvarush Silnistor',
};

/** Side affinity per giver — used by NpcDialog filtering. */
const GIVER_SIDE: Record<string, 'tidal' | 'rooted' | 'anchorless' | null> = {
  'vq-giver-greenwald':  'tidal',
  'vq-giver-dunhammer':  'tidal',
  'vq-giver-worldroot':  'tidal',
  'vq-giver-westmarch':  'tidal',
  'vq-giver-lochmod':    'tidal',
  'vq-giver-darkwald':   'tidal',
  'vq-giver-twilightswamp':'tidal',
  'vq-giver-gloomwood':  'tidal',
  'vq-giver-redrock':    'anchorless',
  'vq-giver-mulhorn':    'anchorless',
  'vq-giver-hollow':     'anchorless',
  'vq-giver-silverleaf': 'anchorless',
  'vq-giver-barrens':    'anchorless',
  // rest neutral
};

let _adapted: Quest[] | null = null;

function transliterateToRu(en: string): string {
  // Crude Latin→Cyrillic for placeholder Russian names. Final translation
  // is a separate task; this is enough to render in the UI.
  const map: Record<string, string> = {
    a:'а',b:'б',c:'к',d:'д',e:'е',f:'ф',g:'г',h:'х',i:'и',j:'дж',k:'к',l:'л',m:'м',
    n:'н',o:'о',p:'п',q:'кв',r:'р',s:'с',t:'т',u:'у',v:'в',w:'в',x:'кс',y:'й',z:'з',
    A:'А',B:'Б',C:'К',D:'Д',E:'Е',F:'Ф',G:'Г',H:'Х',I:'И',J:'Дж',K:'К',L:'Л',M:'М',
    N:'Н',O:'О',P:'П',Q:'Кв',R:'Р',S:'С',T:'Т',U:'У',V:'В',W:'В',X:'Кс',Y:'Й',Z:'З',
  };
  return en.split('').map(ch => map[ch] ?? ch).join('');
}

function adaptVanillaQuest(vq: VQuest): Quest | null {
  const giver = ZONE_GIVER[vq.zone];
  if (!giver) return null; // skip quests in zones we have no NPC for yet

  let type: Quest['type'] = 'kill';
  let target = '';
  let count = 1;
  const firstObj = vq.objectives[0];
  if (!firstObj) {
    // No structured objective — render as a "visit" quest with a placeholder
    // target so the UI still shows it. Better to display flavour text than
    // drop 1000+ quests.
    type = 'visit';
    target = vq.zone;
    count = 1;
  } else if (firstObj.kind === 'kill') {
    type = 'kill';
    target = firstObj.mob;
    count = firstObj.count;
  } else if (firstObj.kind === 'collect') {
    type = 'gather';
    target = firstObj.item;
    count = firstObj.count;
  }

  // Reward — compute baseline XP from level + parse rewards array.
  let xp = 100 + vq.level * 50;
  let gold: number | undefined = 5 + vq.level * 2;
  let itemId: string | undefined;
  for (const r of vq.rewards) {
    if (r.kind === 'gold') gold = Math.max(gold ?? 0, Math.round(r.copper / 100));
    else if (r.kind === 'item') itemId = r.name;
  }

  return {
    id: `vq-${vq.id}`,
    giver: giver.giverId,
    giverName: { ru: '', en: '' }, // filled per-giver below in vanillaQuestsForGiver
    giverSide: GIVER_SIDE[giver.giverId] ?? null,
    giverPos: giver.pos,
    minLevel: Math.max(1, vq.minLevel),
    maxLevel: Math.min(80, vq.level + 5),
    title: { ru: transliterateToRu(vq.title), en: vq.title },
    description: {
      ru: transliterateToRu(vq.objectives_text || vq.details || vq.title),
      en: vq.objectives_text || vq.details || vq.title,
    },
    type,
    objective: { target, count },
    reward: { xp, gold, itemId },
  } as Quest;
}

/** Lazy-load + cache adapted WotLK 3.3.5a quests. Also splice them into the
 *  global QUESTS array so QuestLog/Minimap kill-trackers find them. */
export async function getAdaptedVanillaQuests(): Promise<Quest[]> {
  if (_adapted) return _adapted;
  try {
    const raw = await vanillaData.quests();
    _adapted = raw
      .map(adaptVanillaQuest)
      .filter((q): q is Quest => q !== null);
    // Resolve giver names so QUESTS.find(...) returns useful giverName.
    for (const q of _adapted) {
      const ru = GIVER_NAMES_RU[q.giver] ?? q.giver;
      const en = GIVER_NAMES_EN[q.giver] ?? q.giver;
      q.giverName = { ru, en };
    }
    // Splice into global QUESTS — QuestLog/NpcDialog use QUESTS.find(qq.id).
    const seen = new Set(QUESTS.map((q) => q.id));
    for (const q of _adapted) {
      if (!seen.has(q.id)) QUESTS.push(q);
    }
  } catch (e) {
    console.warn('[vanilla-quest-adapter] failed to load:', e);
    _adapted = [];
  }
  return _adapted;
}

/** Return up to N WotLK 3.3.5a quests appropriate for a given giverId + player level. */
export async function vanillaQuestsForGiver(giverId: string, playerLevel: number, max = 6): Promise<Quest[]> {
  const all = await getAdaptedVanillaQuests();
  // R137: legacy giver-name table for hand-curated NPCs that aren't part
  // of the auto-generated `vq-giver-*` set.
  const legacyRu: Record<string, string> = {
    'old-fisherman': 'Капитан Стивенс',
    'master-smith': 'Кузнец Аргус',
    'storm-oracle': 'Оракул Шторма',
    'caravan-master': 'Десятник Оргнил Соулмарк',
  };
  const legacyEn: Record<string, string> = {
    'old-fisherman': 'Captain Stevens',
    'master-smith': 'Smith Argus',
    'storm-oracle': 'Storm Oracle',
    'caravan-master': 'Foreman Orgnil Soulmark',
  };
  const ru = GIVER_NAMES_RU[giverId] ?? legacyRu[giverId] ?? giverId;
  const en = GIVER_NAMES_EN[giverId] ?? legacyEn[giverId] ?? giverId;
  return all
    .filter(q => q.giver === giverId)
    .filter(q => playerLevel >= q.minLevel && playerLevel <= q.maxLevel + 5)
    .map(q => ({
      ...q,
      giverName: { ru, en },
    }))
    .slice(0, max);
}

/** Public list of all WotLK 3.3.5a quest givers — used by the server to know
 *  where to spawn quest-giver NPCs. */
export const VQ_GIVERS = Object.entries(ZONE_GIVER).reduce((acc, [zone, info]) => {
  if (!acc.find(g => g.giverId === info.giverId)) {
    acc.push({
      giverId: info.giverId,
      pos: info.pos,
      zone,
      side: GIVER_SIDE[info.giverId] ?? null,
      nameRu: GIVER_NAMES_RU[info.giverId] ?? info.giverId,
      nameEn: GIVER_NAMES_EN[info.giverId] ?? info.giverId,
    });
  }
  return acc;
}, [] as Array<{ giverId: string; pos: { x: number; z: number }; zone: string; side: 'tidal' | 'rooted' | 'anchorless' | null; nameRu: string; nameEn: string }>);
